import React, { useEffect, useMemo, useState } from "react";
import Link from "@mui/material/Link";
import FormItem from "@components/FormItem/FormItem.tsx";
import {
  certificatesToClearWhenMLCPIClubChanges,
  certificatesToClearWhenPIClubChanges,
  type VRCertificateType,
} from "@constants/certificates";
import { useNotification } from "@context/NotificationContext";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import {
  clearCertificates,
  FORM_FIELDS,
  getLatestApprovedCertificatesToClear,
} from "@features/vessel-register/utils";
import { getPIClubs } from "@services/referenceDataApi.ts";
import type { PiClub } from "@services/vessel/types.ts";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";

const getPiClubURL = (piClubs: PiClub[], code?: string) => {
  if (!code) return "";
  const club = piClubs?.find((club) => club.piClubCode === code);
  return club && club.piClubURL ? club.piClubURL : "";
};

export const OTHERS = "OTHERS";

const VesselOwnership: React.FC = () => {
  const {
    formData,
    updateFormData,
    watch,
    isRestrictedToCertificatesUpdate,
    vesselDetails: { latestApprovedCertificates } = {},
  } = useOutletContext<VesselOutletContext>();
  const { notify } = useNotification();
  const testIdBase = "register";

  const { data: fetchedPIClubs = [] } = useQuery<PiClub[]>({
    queryKey: ["pi-clubs"],
    queryFn: () => getPIClubs("bff-vessel") as Promise<PiClub[]>,
  });

  const [piClubURL, setPiClubURL] = useState("");
  const [mlcPiClubURL, setMlcPiClubURL] = useState("");

  const piClubsOptions = useMemo(
    () =>
      fetchedPIClubs.map((piClub) => ({
        label: piClub.piClubName || "",
        value: piClub.piClubCode || "",
      })),
    [fetchedPIClubs],
  );

  const piClub = watch(FORM_FIELDS.PI_CLUB);
  const mlcPIClub = watch(FORM_FIELDS.MLC_CLUB);

  const isPIClubOtherSelected = piClub === OTHERS;
  const isMLCOfPIClubOtherSelected = mlcPIClub === OTHERS;

  useEffect(() => {
    if (piClub !== OTHERS) formData.setValue(FORM_FIELDS.PI_CLUB_OTHER, "");
    setPiClubURL(getPiClubURL(fetchedPIClubs, piClub));
  }, [piClub, fetchedPIClubs]);

  useEffect(() => {
    if (mlcPIClub !== OTHERS) formData.setValue(FORM_FIELDS.MLC_CLUB_OTHER, "");
    setMlcPiClubURL(getPiClubURL(fetchedPIClubs, mlcPIClub));
  }, [mlcPIClub, fetchedPIClubs]);

  const getApprovedCertificatesToClearFromList = (eligibleCertificateTypes: VRCertificateType[]) =>
    getLatestApprovedCertificatesToClear(
      formData.getValues(FORM_FIELDS.CERTIFICATES),
      latestApprovedCertificates ?? [],
      eligibleCertificateTypes,
    );

  const notifyCertificatesWillBeInvalidated = (certificates: VRCertificateType[]) => {
    const certList = certificates
      .map((cert) => `- ${t(`vessel-management.certificates.${cert}`)}`)
      .join("\n");

    notify({
      type: "warning",
      title: t("vessel-management.notifications.this-will-invalidate-certificates-list"),
      description: certList,
    });
  };

  return (
    <div data-testid="vessel-ownership-page" className="pb-8">
      <FormProvider {...formData}>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SHIP_OWNER>
              testId={`${testIdBase}-ship-owner-input`}
              maxChars={50}
              label={t("vessel-management.shipOwner")}
              name={FORM_FIELDS.SHIP_OWNER}
              onFieldChange={updateFormData}
              disabled={isRestrictedToCertificatesUpdate}
            />

            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SHIP_CHARTERER>
              testId={`${testIdBase}-ship-charterer-input`}
              label={t("vessel-management.shipCharterer")}
              maxChars={150}
              name={FORM_FIELDS.SHIP_CHARTERER}
              onFieldChange={updateFormData}
              disabled={isRestrictedToCertificatesUpdate}
            />

            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SHIPS_MANAGEMENT>
              testId={`${testIdBase}-ships-management-input`}
              label={t("vessel-management.shipsManagement")}
              maxChars={150}
              onFieldChange={updateFormData}
              name={FORM_FIELDS.SHIPS_MANAGEMENT}
              disabled={isRestrictedToCertificatesUpdate}
            />

            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SHIPS_REGISTERED_OWNER>
              testId={`${testIdBase}-ships-registered-owner-input`}
              label={t("vessel-management.shipsRegisteredOwner")}
              maxChars={50}
              onFieldChange={updateFormData}
              name={FORM_FIELDS.SHIPS_REGISTERED_OWNER}
              disabled={isRestrictedToCertificatesUpdate}
            />

            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.PI_CLUB>
              testId={`${testIdBase}-name-of-p&i-club-options`}
              name={FORM_FIELDS.PI_CLUB}
              label={t("vessel-management.name-ofp&i-club")}
              type="autocomplete"
              options={piClubsOptions}
              onFieldChange={(k, v) => {
                if (latestApprovedCertificates && latestApprovedCertificates.length > 0) {
                  const certsToClear = getApprovedCertificatesToClearFromList(
                    certificatesToClearWhenPIClubChanges,
                  );
                  if (certsToClear.length) {
                    notifyCertificatesWillBeInvalidated(certsToClear);
                    clearCertificates(formData.setValue, certsToClear, updateFormData);
                  }
                }
                updateFormData(k, v);
              }}
              disabled={isRestrictedToCertificatesUpdate}
            />

            {isPIClubOtherSelected ? (
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.PI_CLUB_OTHER>
                testId={`${testIdBase}-ships-pi-club-other`}
                label={t("vessel-management.other-p&i-club")}
                onFieldChange={updateFormData}
                name={FORM_FIELDS.PI_CLUB_OTHER}
                disabled={isRestrictedToCertificatesUpdate}
              />
            ) : (
              !isPIClubOtherSelected &&
              (piClubURL ? (
                <div className="w-1/2 flex items-center">
                  <Link href={piClubURL}>{piClubURL}</Link>
                </div>
              ) : (
                <div className="w-1/2" />
              ))
            )}

            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.MLC_CLUB>
              testId={`${testIdBase}-name-mlc-of-p&i-club-options`}
              name={FORM_FIELDS.MLC_CLUB}
              label={t("vessel-management.name-mlc-of-p&i-club")}
              type="autocomplete"
              options={piClubsOptions}
              onFieldChange={(k, v) => {
                if (latestApprovedCertificates && latestApprovedCertificates.length > 0) {
                  const certsToClear = getApprovedCertificatesToClearFromList(
                    certificatesToClearWhenMLCPIClubChanges,
                  );
                  if (certsToClear.length) {
                    notifyCertificatesWillBeInvalidated(certsToClear);
                    clearCertificates(formData.setValue, certsToClear, updateFormData);
                  }
                }
                updateFormData(k, v);
              }}
              disabled={isRestrictedToCertificatesUpdate}
            />

            {isMLCOfPIClubOtherSelected && (
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.MLC_CLUB_OTHER>
                testId={`${testIdBase}-mlc-of-pi-club-other`}
                label={t("vessel-management.other-mlc-p&i-club-name")}
                onFieldChange={updateFormData}
                name={FORM_FIELDS.MLC_CLUB_OTHER}
                disabled={isRestrictedToCertificatesUpdate}
              />
            )}
            {!isMLCOfPIClubOtherSelected && mlcPiClubURL ? (
              <div className="w-1/2 flex items-center">
                <Link href={mlcPiClubURL}>{mlcPiClubURL}</Link>
              </div>
            ) : (
              <div className="w-1/2"></div>
            )}
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default VesselOwnership;
